import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, Sparkle, Calendar, Share2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { UpgradeToPlusButton } from "@/components/plus/UpgradeToPlusButton";
import { WaitlistDialog } from "@/components/common/WaitlistDialog";
import { AddMedicationDialog } from "@/components/common/AddMedicationDialog";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ProfileSwitcher } from "@/components/ProfileSwitcher";
import { ScheduleMenuButtonWithDialogs } from "@/components/schedule/ScheduleMenuButton";
import { ShareScheduleButtonWithErrorHandling as ShareScheduleButton } from "@/components/schedule/ShareScheduleButton";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import logoMark from '@/assets/logo.svg';

/**
 * OptimizedHeader - A lightweight version of the header component
 * 
 * This component provides essential navigation in a two-level structure:
 * - Primary (top): Logo, Schedule, CTA buttons, Profile switcher
 * - Secondary (bottom): About, Features, Caregivers, How It Works, FAQ, Pricing, Waitlist
 */
export function OptimizedHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAddMedicationOpen, setIsAddMedicationOpen] = useState(false);
  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [isDesktopSize, setIsDesktopSize] = useState(false);
  const scheduleComponentRef = useRef<HTMLDivElement>(null);
  
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const statsButtonRef = useRef<HTMLButtonElement>(null);

  // Handle scroll behavior with improved performance
  useEffect(() => {
    // Use this flag to conditionally run browser-specific code without breaking hooks order
    const isBrowser = typeof window !== 'undefined';
    
    // Early return with empty cleanup for non-browser environments
    if (!isBrowser) {
      return () => {};
    }
    
    // Variables for scroll handling
    let lastScrollTop = 0;
    let scrollTimer: ReturnType<typeof setTimeout> | null = null;
    let resizeTimer: ReturnType<typeof setTimeout> | null = null;
    let ticking = false; // Flag to prevent multiple rAF calls
    
    /**
     * Handle scroll events with throttling via requestAnimationFrame
     * @returns void
     */
    const handleScroll = (): void => {
      const scrollPosition = window.scrollY;
      
      // Use requestAnimationFrame for smoother performance
      if (!ticking) {
        window.requestAnimationFrame(() => {
          // Update state based on scroll position
          setHasScrolled(scrollPosition > 20);
          setIsScrolled(scrollPosition > 5);
          
          // Update last known position
          lastScrollTop = scrollPosition;
          ticking = false;
        });
        
        ticking = true;
      }
    };
    
    /**
     * Check if viewport is desktop size with debouncing
     * @returns void
     */
    const checkDesktopSize = (): void => {
      // Clear existing timer
      if (resizeTimer) clearTimeout(resizeTimer);
      
      // Set a timer to update state after resize events settle (100ms debounce)
      resizeTimer = setTimeout(() => {
        // Check if viewport matches desktop size
        const isDesktop = window.matchMedia('(min-width: 640px)').matches;
        setIsDesktopSize(isDesktop);
        
        // Force trigger a scroll check after resize
        handleScroll();
      }, 100);
    };
    
    // Add event listeners
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', checkDesktopSize);
    
    // Run initial checks
    handleScroll();
    checkDesktopSize();
    
    // Run a second check after a short delay to ensure accuracy
    const initialCheckTimer = setTimeout(() => {
      handleScroll();
      checkDesktopSize();
    }, 100);
    
    // Return cleanup function
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkDesktopSize);
      if (scrollTimer) clearTimeout(scrollTimer);
      if (resizeTimer) clearTimeout(resizeTimer);
      clearTimeout(initialCheckTimer);
    };
  }, []);

  // Reset mobile menu when screen size changes
  useEffect(() => {
    if (!isMobile && isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  }, [isMobile, isMobileMenuOpen]);
  
  const toggleAddMedicationDialog = () => {
    setIsAddMedicationOpen(!isAddMedicationOpen);
    if (isMobileMenuOpen) setIsMobileMenuOpen(false);
  };
  
  const toggleWaitlistDialog = () => {
    setIsWaitlistOpen(!isWaitlistOpen);
    if (isMobileMenuOpen) setIsMobileMenuOpen(false);
  };
  
  const handleScheduleClick = () => {
    // Navigate to home page first, then scroll to schedule
    navigate('/', { replace: false });
    
    // Wait for navigation to complete before scrolling
    setTimeout(() => {
      const scheduleElement = document.getElementById('medication-schedule');
      if (scheduleElement) {
        scheduleElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
    
    if (isMobileMenuOpen) setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* Add a taller spacer div to prevent content from being hidden under the fixed header */}
      <div className="h-[70px] md:h-[120px]" data-component-name="OptimizedHeader"></div>
      <header
        className={cn(
          "fixed top-0 left-0 right-0 z-[100] transition-all duration-300 flex flex-col w-full",
          isScrolled
            ? "bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-md"
            : "bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm"
        )}
      >
        {/* Primary Header - Logo, Schedule, CTA buttons, Profile switcher */}
        <div className="container mx-auto px-4 py-3" data-testid="primary-header-container">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <span className="text-2xl font-bold tracking-tight">
                {isMobile ? (
                  <div className="flex items-start gap-2">
                    <div className="flex items-center justify-center bg-white rounded-full h-8 w-8 shadow-sm mt-1.5">
                      <img src={logoMark} alt="Meduler Logo" className="h-6 w-6" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-purple-600 leading-6 pt-1">Meduler</span>
                      <span className="text-[10px] text-purple-400 font-medium tracking-wide -mt-1">ALPHA</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col">
                    <div className="flex items-baseline">
                      <span className="text-purple-600">Meduler</span>
                      <span className="ml-2 text-[12px] text-purple-400 font-medium tracking-wide">ALPHA</span>
                    </div>                  
                    <div className="flex items-baseline">
                      <span className="text-sm text-purple-600">Med</span>
                      <span className="text-sm text-gray-400">ication </span>
                      <span className="text-sm text-purple-600">Sched</span>
                      <span className="text-sm text-gray-400">uler</span>
                    </div>
                  </div>
                )}
              </span>
            </Link>

            {/* Center items - Schedule button and CTA pill */}
            <div className="hidden md:flex items-center gap-6 flex-1 justify-center">
              <button
                onClick={handleScheduleClick}
                className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors px-3 py-2 bg-transparent border-none cursor-pointer"
              >
                Schedule
              </button>

              <div className="mb-0">
                <div className="flex rounded-full overflow-hidden border border-purple-200 shadow-sm">
                  <button 
                    className="px-4 py-2 text-purple-600 font-medium bg-white hover:bg-purple-50 transition-colors"
                    onClick={toggleAddMedicationDialog}
                    aria-label="Start using Meduler for free"
                  >
                    <span>Start for Free</span>
                  </button>
                  <button 
                    className="bg-purple-600 hover:bg-purple-700 py-2 px-4 text-white font-medium transition-colors"
                    onClick={toggleAddMedicationDialog}
                    aria-label="Add your first medication in just 2 minutes"
                  >
                    <span>Add Meds</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Right-side Controls - Mobile Menu Button and Profile Switcher */}
            <div className="flex items-center gap-3">
              {/* Mobile menu button shown only on mobile */}
              {isMobile && (
                <button
                  type="button"
                  aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                  className="ml-1 p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 md:hidden"
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                >
                  {isMobileMenuOpen ? (
                    <X className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Menu className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
              )}
              
              {/* Modern floating menu bar with enhanced Profile Switcher and buttons */}
              {!isMobile && (
                <div 
                  className={cn(
                    "flex items-center justify-between max-w-full z-[101] backdrop-blur-xl transition-transform duration-200 ease-out group",
                    hasScrolled && isDesktopSize 
                      ? "fixed shadow-[0_10px_40px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.25)] bg-white/90 dark:bg-gray-900/95 border border-primary/20 animate-float-in" 
                      : "relative bg-gradient-to-r from-white/95 to-white/98 dark:from-gray-800/95 dark:to-gray-800/98 border border-gray-100/70 dark:border-gray-700/60 shadow-sm",
                    hasScrolled && isDesktopSize ? "p-2.5 sm:p-3 rounded-2xl" : "p-3 rounded-full"
                  )}
                  style={{
                    position: hasScrolled && isDesktopSize ? "fixed" : "relative",
                    right: hasScrolled && isDesktopSize ? "20px" : "auto",
                    top: hasScrolled && isDesktopSize ? (window.innerWidth >= 768 ? "20px" : "12px") : "auto",
                    transition: "all 0.15s cubic-bezier(0.3, 0, 0.2, 1)", /* Snappier transition */
                    willChange: "transform, opacity, box-shadow, position, top, right", /* Performance optimization */
                    transform: hasScrolled && isDesktopSize ? 
                      "translateY(0) translateZ(0)" : 
                      "translateY(0) translateZ(0)",
                    zIndex: 1001, /* Above other elements */
                    backdropFilter: "blur(10px)", /* Enhanced blur effect */
                    WebkitBackdropFilter: "blur(10px)", /* For Safari */
                    boxShadow: hasScrolled && isDesktopSize ? 
                      "0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.01)" : 
                      "0 1px 3px rgba(0, 0, 0, 0.04)",
                  }}
                  data-component-name="OptimizedHeader"
                  data-scrolled={hasScrolled ? 'true' : 'false'}
                  data-desktop={isDesktopSize ? 'true' : 'false'}
                  aria-label="Header menu"
                  role="toolbar"
                  tabIndex={0}
                >
                  {/* Modern floating indicator - subtle accent that follows scroll */}
                  {hasScrolled && isDesktopSize && (
                    <div className="absolute inset-0 -z-10 rounded-2xl overflow-hidden" aria-hidden="true">
                      {/* Gradient background effect */}
                      <div className="absolute inset-0 bg-gradient-to-tr from-primary/5 via-background to-primary/5 opacity-70 dark:opacity-40 pointer-events-none" />
                      
                      {/* Animated accent line at the top */}
                      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary/70 to-transparent animate-gradient-x" />
                      
                      {/* Subtle glass effect border - only visible in light mode */}
                      <div className="absolute inset-0 bg-gradient-to-b from-white/40 to-transparent opacity-50 dark:opacity-0 pointer-events-none" />
                    </div>
                  )}
                  <div className="flex gap-2 sm:gap-3 md:gap-4 flex-wrap justify-end items-center transition-all duration-200 ease-in-out">
                    {/* Visual indicator with dot status - always visible */}
                    <div className="mr-1 text-xs font-medium text-primary/80 flex items-center gap-1 animate-fade-in">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary/80 animate-pulse" />
                      {/* Menu text removed as requested */}
                    </div>
                    {/* Profile Switcher - enhanced with better hover effects */}
                    <div 
                      className={cn(
                        "flex-shrink-0 transition-all", 
                        hasScrolled && isDesktopSize ? "duration-150" : "duration-200",
                        "hover:scale-105 hover:rotate-1"
                      )}
                      title="Switch profile"
                      style={{
                        filter: !hasScrolled || !isDesktopSize ? 
                          "drop-shadow(0 4px 3px rgb(0 0 0 / 0.07)) drop-shadow(0 2px 2px rgb(0 0 0 / 0.06))" : 
                          "none"
                      }}
                    >
                      <ProfileSwitcher compact={true} />
                    </div>
                    
                    {/* Share Button - modernized with glass effect on scroll */}
                    <div className={cn(
                      "flex-shrink-0 transition-all transform",
                      hasScrolled && isDesktopSize ? "duration-150 hover:scale-105" : "duration-300 hover:scale-110",
                      "hover:-rotate-1"
                    )}>
                      <ShareScheduleButton 
                        targetRef={scheduleComponentRef} 
                        medications={[]} 
                        className={cn(
                          "h-12 w-12 rounded-full flex items-center justify-center transform transition-all duration-300",
                          "hover:shadow-lg hover:scale-110",
                          hasScrolled && isDesktopSize 
                            ? 'bg-white/75 hover:bg-white/90 dark:bg-gray-800/75 dark:hover:bg-gray-800/90 border border-primary/30 hover:border-primary/50 shadow-md' 
                            : 'bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 border-2 border-gray-200 dark:border-gray-700'
                        )}
                        icon={<svg 
                          width="22" 
                          height="22" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          className={cn(
                            "stroke-primary/80 group-hover:stroke-primary transition-all duration-300",
                            "text-primary transition-all duration-300",
                            hasScrolled && isDesktopSize ? "text-primary" : "text-primary/80"
                          )}
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>}
                      />
                    </div>
                    
                    {/* Menu Button - modernized with glass effect and improved animations */}
                    <Button
                      variant="outline"
                      size="icon"
                      className={cn(
                        "h-12 w-12 rounded-full flex-shrink-0 flex items-center justify-center",
                        "transform hover:scale-110 hover:rotate-1 hover:shadow-lg transition-all duration-300",
                        "focus:ring-2 focus:ring-primary/40 focus:ring-offset-2",
                        hasScrolled && isDesktopSize 
                          ? 'bg-white/70 hover:bg-white/90 dark:bg-gray-800/70 dark:hover:bg-gray-800/90 border border-primary/40 hover:border-primary/60 shadow-md' 
                          : 'bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 border-2 border-gray-200 dark:border-gray-700'
                      )}
                      style={{
                        backdropFilter: hasScrolled ? "blur(8px)" : "none"
                      }}
                      aria-label="Menu options"
                      asChild
                    >
                      <div>
                        <ScheduleMenuButtonWithDialogs
                          onRefreshRequested={async () => {
                            // Return a Promise to satisfy the expected type
                            toast.success("Page Refreshed", {
                              description: "Your page has been refreshed with the latest data.",
                              duration: 3000,
                            });
                            return Promise.resolve();
                          }}
                          hideAddMedicationButton={true}
                        />
                      </div>
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Secondary Header - About, Features, Caregivers, etc. */}
        <div className="w-full border-t border-gray-100/70 dark:border-gray-800/70 hidden md:block bg-white/60 dark:bg-gray-900/70 backdrop-blur-sm">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-center space-x-2 py-2.5">
              <Link
                to="/about"
                className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-all px-3 py-1.5 rounded-full hover:bg-purple-50 dark:hover:bg-gray-800"
              >
                About
              </Link>
              <Link
                to="/features"
                className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-all px-3 py-1.5 rounded-full hover:bg-purple-50 dark:hover:bg-gray-800"
              >
                Features
              </Link>
              <Link
                to="/caregivers"
                className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-all px-3 py-1.5 rounded-full hover:bg-purple-50 dark:hover:bg-gray-800"
              >
                Caregivers
              </Link>
              <Link
                to="/how-it-works"
                className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-all px-3 py-1.5 rounded-full hover:bg-purple-50 dark:hover:bg-gray-800"
              >
                How It Works
              </Link>
              <Link
                to="/faq"
                className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-all px-3 py-1.5 rounded-full hover:bg-purple-50 dark:hover:bg-gray-800"
              >
                FAQ
              </Link>
              <Link
                to="/pricing"
                className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-all px-3 py-1.5 rounded-full hover:bg-purple-50 dark:hover:bg-gray-800"
                onClick={() => {
                  if (isMobileMenuOpen) setIsMobileMenuOpen(false);
                }}
              >
                Pricing
              </Link>
              <Link
                to="/pricing"
                className="text-sm font-medium text-purple-600 hover:text-purple-700 transition-all px-3.5 py-1.5 flex items-center gap-1.5 bg-purple-50 dark:bg-purple-900/20 rounded-full hover:bg-purple-100 dark:hover:bg-purple-900/30 border border-purple-100 dark:border-purple-800/30"
                onClick={() => {
                  if (isMobileMenuOpen) setIsMobileMenuOpen(false);
                  // Focus on the email input after navigation
                  setTimeout(() => {
                    const emailInput = document.querySelector('.card-footer-email-section input[type="email"]');
                    if (emailInput) {
                      (emailInput as HTMLInputElement).focus();
                    }
                  }, 100);
                }}
              >
                <span>Waitlist for Plus</span>
                <Sparkle className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu - Expanded with all new options */}
        {isMobile && isMobileMenuOpen && (
          <div className="md:hidden pb-4 bg-white dark:bg-gray-900 rounded-b-lg shadow-lg border-b border-x border-purple-100 absolute top-full left-0 right-0 z-50">
            <div className="flex justify-center py-3 border-b dark:border-gray-800 items-center gap-2">
              <span className="text-sm text-gray-600 font-medium">Switch Profile</span>
              <ProfileSwitcher />
            </div>
            <div className="flex flex-col space-y-2 p-4">
              {/* Primary navigation items */}
              <button
                onClick={handleScheduleClick}
                className="text-left text-base font-medium text-gray-600 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors py-2 px-2 bg-transparent border-none cursor-pointer w-full"
              >
                Schedule
              </button>
              
              <Link
                onClick={() => {
                  toggleAddMedicationDialog();
                  setIsMobileMenuOpen(false);
                }}
                className="text-base font-medium text-gray-600 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors py-2 px-2"
                to="#"
              >
                Add Medications
              </Link>
              
              {/* Divider */}
              <div className="border-t border-gray-100 my-1"></div>
              
              {/* Secondary navigation items */}
              <Link
                to="/about"
                className="text-base font-medium text-gray-600 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors py-2 px-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                About
              </Link>
              
              <Link
                to="/features"
                className="text-base font-medium text-gray-600 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors py-2 px-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Features
              </Link>
              
              <Link
                to="/caregivers"
                className="text-base font-medium text-gray-600 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors py-2 px-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Caregivers
              </Link>
              
              <Link
                to="/how-it-works"
                className="text-base font-medium text-gray-600 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors py-2 px-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                How It Works
              </Link>
              
              <Link
                to="/faq"
                className="text-base font-medium text-gray-600 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors py-2 px-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                FAQ
              </Link>
              
              <Link
                to="/pricing"
                className="text-base font-medium text-gray-600 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors py-2 px-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Pricing
              </Link>
              
              <div className="py-2">
                <UpgradeToPlusButton
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    setTimeout(() => {
                      const emailSection = document.querySelector('.card-footer-email-section');
                      if (emailSection) {
                        emailSection.scrollIntoView({ behavior: 'smooth' });
                        const emailInput = emailSection.querySelector('input[type="email"]');
                        if (emailInput instanceof HTMLInputElement) {
                          emailInput.focus();
                        }
                      }
                    }, 100);
                  }}
                  variant="purple"
                  size="default"
                />
              </div>
            </div>
          </div>
        )}

        {/* Dialogs */}
        <Dialog open={isWaitlistOpen} onOpenChange={setIsWaitlistOpen}>
          <DialogContent>
            {/* ACCESSIBILITY FIX: Ensure DialogTitle is the first direct child of DialogContent */}
            <DialogTitle id="waitlist-dialog-title" className="text-lg font-semibold">
              Join Our Waitlist
            </DialogTitle>
            {/* The WaitlistDialog content is rendered inside this accessible wrapper */}
            <WaitlistDialog open={isWaitlistOpen} onOpenChange={setIsWaitlistOpen} />
          </DialogContent>
        </Dialog>
        
        <Dialog open={isAddMedicationOpen} onOpenChange={setIsAddMedicationOpen}>
          <DialogContent 
            className="p-0 overflow-hidden border-none rounded-lg bg-white dark:bg-gray-950 shadow-xl max-h-[90vh] max-w-4xl data-[state=open]:translate-y-0 data-[state=open]:!top-2 data-[state=open]:slide-in-from-top-2"
          >
            {/* ACCESSIBILITY FIX: Ensure DialogTitle is the first direct child of DialogContent */}
            <DialogTitle id="add-medication-dialog-title" className="sr-only">Add Medication</DialogTitle>
            {/* Embed the dialog inside our wrapper with proper accessibility structure */}
            <div className="w-full h-full rounded-lg overflow-hidden">
              <AddMedicationDialog
                open={isAddMedicationOpen}
                onOpenChange={setIsAddMedicationOpen}
                insideDialogContent={true}
              />
            </div>
          </DialogContent>
        </Dialog>
      </header>
    </>
  );
}
